import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import posthog from 'posthog-js'
import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import { ScoreExplainer, ScoreMeter } from 'components/memberScore'
import { getAdminMemberRecord, getLoanCandidates } from 'services/fund'
import { faFull, faPlain, longJalali } from 'utilities/helper/format'
import PortionGrid from './components/PortionGrid'
import LoanList from './components/LoanList'
import CapitalChart from './components/CapitalChart'
import { CapitalCard, GuaranteesCard, LoansSection, TransactionsCard } from './components/Blocks'

/**
 * کارنامهٔ عضو — what the manager sees when they open a member (pwa#36).
 *
 * The old screen («سابقه مالی عضو») showed the manager exactly what the member
 * sees of themselves: a grey box of «label: value» lines. That answers «what
 * are this person's numbers». A manager is asking something else — «what kind
 * of member is this» — and no arrangement of the same totals answers it.
 *
 * Order is by decreasing urgency, so the two facts a manager acts on (the
 * score, and how much of the fund's money this person is holding) need no
 * scrolling.
 *
 * امتیاز comes from `loan-candidates`, not from this member's own endpoint:
 * rank and percentile are positions in the WHOLE fund and cannot be computed
 * for one person. That query is shared with the grant wizard, so a manager
 * moving between the two screens pays for the scan once.
 */
export default function MemberRecord() {
    const { moneyPool, poolship } = useParams()
    const navigate = useNavigate()
    const [explainerOpen, setExplainerOpen] = useState(false)

    const record = useQuery(
        ['admin-member-record', moneyPool, poolship],
        getAdminMemberRecord,
        {
            onError: (error: any) =>
                posthog.capture('member_record_load_failed', {
                    fund_id: moneyPool,
                    poolship_id: poolship,
                    status: error?.response?.status,
                }),
        }
    )
    const candidates = useQuery(
        ['loan-candidates', moneyPool, false, false],
        getLoanCandidates,
        { staleTime: 5 * 60 * 1000 }
    )

    const scoreRow = candidates.data?.results?.find(
        (row) => String(row.poolship_id) === String(poolship)
    )

    useEffect(() => {
        posthog.capture('member_record_viewed', {
            fund_id: moneyPool,
            poolship_id: poolship,
        })
    }, [moneyPool, poolship])

    const data = record.data

    return (
        <div className=" min-h-screen bg-canvas pb-6">
            <Header title="کارنامهٔ عضو" />

            {record.isLoading || !data ? (
                <DotsLoading />
            ) : (
                <div className=" flex flex-col gap-3 p-3">
                    {/* identity */}
                    <section className=" rounded-2xl border border-stroke bg-white p-4 shadow-card">
                        <div className=" flex items-center gap-3">
                            <span className=" flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-soft text-base font-bold text-brand-deep">
                                {(data.member_name || '؟').trim().charAt(0)}
                            </span>
                            <div className=" min-w-0 flex-1">
                                <p className=" truncate text-base font-bold text-heading">
                                    {data.member_name}
                                </p>
                                <p dir="ltr" className=" mt-0.5 text-right text-xs text-text-secondary">
                                    {data.phone_number}
                                </p>
                            </div>
                        </div>
                        <div className=" mt-3 grid grid-cols-2 gap-2.5 border-t border-divider pt-3">
                            <div>
                                <p className=" text-[11px] text-text-secondary">
                                    عضو از
                                </p>
                                <p className=" mt-0.5 text-xs font-bold text-text-body">
                                    {longJalali(data.joined_date)}
                                </p>
                            </div>
                            <div>
                                <p className=" text-[11px] text-text-secondary">
                                    ضامن عضویتش
                                </p>
                                <p className=" mt-0.5 text-xs font-bold text-text-body">
                                    {data.guarantor?.member_name || 'ثبت نشده'}
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* score — the shared component, so #34-style changes land once */}
                    {scoreRow && (
                        <section className=" rounded-2xl border border-stroke bg-white p-4 shadow-card">
                            <p className=" text-sm font-bold text-text-body">
                                امتیاز عضو
                            </p>
                            <div className=" mt-2 flex items-baseline gap-1.5">
                                <span className=" text-2xl font-bold text-heading">
                                    رتبهٔ {faPlain.format(scoreRow.rank)}
                                </span>
                                <span className=" text-xs text-text-secondary">
                                    از {faPlain.format(candidates.data?.members_count ?? 0)} عضو
                                </span>
                            </div>
                            <div className=" mt-2.5">
                                <ScoreMeter percentile={scoreRow.percentile} />
                            </div>
                            <button
                                type="button"
                                onClick={() => {
                                    posthog.capture('member_record_score_opened', {
                                        fund_id: moneyPool,
                                        poolship_id: poolship,
                                    })
                                    setExplainerOpen(true)
                                }}
                                className=" mt-3 flex min-h-[44px] items-center gap-1.5 rounded-lg bg-canvas px-3.5 py-2.5 text-xs font-bold text-brand-deep"
                            >
                                جزئیات محاسبه
                            </button>
                        </section>
                    )}

                    <CapitalCard record={data} />
                    <CapitalChart series={data.capital_series} />
                    <PortionGrid grid={data.portion_grid} />

                    <LoansSection record={data}>
                        <LoanList loans={data.loan_list ?? []} />
                    </LoansSection>

                    <GuaranteesCard data={data.guarantees} moneypoolId={moneyPool} />
                    <TransactionsCard rows={data.transactions} />

                    <button
                        type="button"
                        onClick={() => {
                            posthog.capture('member_record_grant_clicked', {
                                fund_id: moneyPool,
                                poolship_id: poolship,
                            })
                            // «به این عضو» — so the wizard must not open on
                            // «به چه کسی؟». The candidate row is the same
                            // object step 1 would have handed it, taken from
                            // the query both screens already share.
                            navigate(`/loans/management/grant/${moneyPool}`, {
                                state: {
                                    borrower: scoreRow,
                                    source: 'member_record',
                                },
                            })
                        }}
                        className=" min-h-[44px] rounded-[10px] bg-brand p-3 text-sm font-bold text-white"
                    >
                        اعطای وام به این عضو
                    </button>
                </div>
            )}

            {scoreRow && (
                <ScoreExplainer
                    show={explainerOpen}
                    onHide={() => setExplainerOpen(false)}
                    candidate={scoreRow}
                    overdueMultiplier={candidates.data?.overdue_multiplier ?? 3}
                    earlyMultiplier={candidates.data?.early_multiplier ?? 3}
                    membersCount={candidates.data?.members_count ?? 0}
                    inflationApplied={false}
                />
            )}
        </div>
    )
}
